import { useMemo, useState } from 'react'
import { useParams } from 'react-router'
import toInteger from 'lodash/toInteger'
import { operationalAmountLabel } from '@klear/operational/presentation'
import { isEventLine } from '@klear/payment/line-item'
import {
  PERMISSION_ACTION_ADD,
  PERMISSION_ACTION_COMPLETE,
  PERMISSION_ACTION_EDIT,
  PERMISSION_ACTION_PUBLISH,
  PERMISSION_ENTITY_PAYMENT
} from '@klear/constants'
import { PaymentView } from '@klear/order-event-ui/event-forms/payment-form'
import { EventFieldGrid, type EventField } from '@klear/order-event-ui/event-forms/event-field-grid'
import { PAYMENT_NETWORK_ACH, PAYMENT_NETWORK_WIRE } from '@klear/bank-account/constants'
import type { Payable } from '@klear/payable/payable'
import { usePaymentReceipts } from '@/app/payable/use-payment-receipts'
import { operationalSupplierIdAsNumber } from '@klear/operational'
import { useOperationals } from '@klear/ui/hooks/use-operationals'
import { useAuth } from '@klear/ui/contexts/auth-context'
import { Card } from '@klear/ui/components/card'
import { Button } from '@klear/ui/components/button'
import { Skeleton } from '@klear/ui/components/skeleton'
import { Tooltip, TooltipContent, TooltipTrigger } from '@klear/ui/components/tooltip'
import type { WizardStepComponentProps } from '@klear/ui/wizard/flow'
import type { PayableStepCtx } from '@/app/payable/workflow/PayableWizardContext'
import { isPayableLeaveDirty } from '@/app/payable/workflow/payable-form-state'
import { coveredEventDetails, coveredLineMeta } from '@/app/payable/workflow/payable-covered-lines'
import { ReceiptDownloadBadge } from '@/app/payable/workflow/payment-step-fields'
import {
  PayableCommitConfirmModal,
  type PayableCommitConfirmAction
} from '@/app/payable/workflow/PayableCommitConfirmModal'
import { PayableMarkPaidModal, type PayableMarkPaidFormValues } from '@/app/payables/payable-mark-paid-modal'

const PERMISSION_DENIED_LABEL = "You don't have permission for this action"

function PermissionGatedButton({
  label,
  permitted,
  workflowReady,
  pending,
  onClick,
  variant
}: {
  label: string
  permitted: boolean
  workflowReady: boolean
  pending: boolean
  onClick: () => void
  variant?: 'outline' | 'default'
}) {
  const button = (
    <Button variant={variant} onClick={onClick} disabled={!permitted || !workflowReady || pending}>
      {pending ? 'Working...' : label}
    </Button>
  )
  if (permitted) return button
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0} className="inline-flex">
          {button}
        </span>
      </TooltipTrigger>
      <TooltipContent>{PERMISSION_DENIED_LABEL}</TooltipContent>
    </Tooltip>
  )
}

function PayableDetails({ payable }: { payable: Payable }) {
  const { traderId: traderIdParam } = useParams<{ traderId?: string }>()
  const traderId = toInteger(traderIdParam)
  const paymentId = payable.paymentId ?? payable.id
  const receiptsFor = usePaymentReceipts([paymentId], traderId)
  const receipts = receiptsFor(paymentId)
  const isBankNetwork = payable.network === PAYMENT_NETWORK_ACH || payable.network === PAYMENT_NETWORK_WIRE

  const fields: EventField[] = [
    { label: 'Supplier', value: payable.supplierName ?? null },
    ...(isBankNetwork
      ? [
          { label: 'Payment reference', value: payable.statementDescriptor ?? null },
          { label: 'Email supplier when sent', value: payable.notifySupplier ? 'Yes' : 'No' }
        ]
      : [])
  ]

  return (
    <Card className="p-6 space-y-4">
      <h2 className="text-lg font-semibold text-foreground">Details</h2>
      <EventFieldGrid fields={fields} />
      <EventFieldGrid fields={[{ label: 'Notes', value: payable.notes ?? null }]} />
      <EventFieldGrid
        fields={[
          {
            label: 'Documents',
            value:
              receipts.length > 0 ? (
                <div className="flex flex-wrap gap-2">
                  {receipts.map((receipt) => (
                    <ReceiptDownloadBadge key={receipt.id} name={receipt.name} url={receipt.url} />
                  ))}
                </div>
              ) : null
          }
        ]}
      />
    </Card>
  )
}

export default function ReviewStep({ ctx }: WizardStepComponentProps<PayableStepCtx>) {
  const { user } = useAuth()
  const {
    form,
    dirty,
    previewResult,
    formError,
    isPreviewing,
    isCommitting,
    isLoadingPayable,
    handleSave,
    handlePublish,
    handleComplete,
    handleMarkPaid,
    readOnly
  } = ctx

  const coveredIds = form.operationalEventIds
  const { operationals: coveredOperationals } = useOperationals(
    { orderEventId: coveredIds },
    { enabled: coveredIds.length > 0 }
  )
  const detailByEventId = useMemo(() => coveredEventDetails(coveredOperationals), [coveredOperationals])

  const hasPublishPerm = user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_PUBLISH)
  const hasCompletePerm = user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_COMPLETE)
  const hasSavePerm =
    form.payableId != null && form.payableId > 0
      ? user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_EDIT)
      : user.hasPermission(PERMISSION_ENTITY_PAYMENT, PERMISSION_ACTION_ADD)

  const canPersistDraft = previewResult?.canSave ?? false
  const canSave = canPersistDraft && isPayableLeaveDirty(dirty)
  const canSchedule = previewResult?.canPublish ?? false
  const canPayNow = previewResult?.canPayNow ?? false
  const canMarkPaid = !previewResult?.isSent && (canPersistDraft || canPayNow || canSchedule)
  const pending = isPreviewing || isCommitting
  const [confirmAction, setConfirmAction] = useState<PayableCommitConfirmAction | null>(null)
  const [markPaidOpen, setMarkPaidOpen] = useState(false)

  const operational = previewResult?.operationals[0]
  const lineItems = previewResult?.lineItems ?? []
  const eventLineCount = lineItems.filter(isEventLine).length

  const hasComponents = form.operationalEventIds.length > 0 || form.items.length > 0
  const hasPayment = form.payment != null || (form.payableId != null && form.payableId > 0)

  if (!hasComponents || !hasPayment) {
    return <div className="text-sm text-muted-foreground">Complete the previous steps first.</div>
  }

  if ((isPreviewing || isLoadingPayable) && !previewResult) {
    return (
      <div className="space-y-4 w-full max-w-3xl">
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-32 w-full" />
      </div>
    )
  }

  if (!previewResult) {
    if (readOnly && isLoadingPayable) {
      return (
        <div className="space-y-4 w-full max-w-3xl">
          <Skeleton className="h-32 w-full" />
          <Skeleton className="h-32 w-full" />
        </div>
      )
    }
    return formError ? (
      <div className="text-sm text-destructive">{formError}</div>
    ) : (
      <div className="text-sm text-muted-foreground">Preview is not available. Go back and check your entries.</div>
    )
  }

  const parentSupplierId = operationalSupplierIdAsNumber(operational?.supplierId ?? previewResult.supplierId)

  const onMarkPaidConfirm = async (values: PayableMarkPaidFormValues) => {
    const result = await handleMarkPaid(values)
    if (result) {
      setMarkPaidOpen(false)
    }
  }

  return (
    <div className="space-y-6 w-full max-w-3xl">
      {lineItems.length > 0 ? (
        <Card className="p-6 space-y-3">
          <div>
            <h2 className="text-lg font-semibold text-foreground">What this payment covers</h2>
            <p className="text-sm text-muted-foreground">
              {lineItems.length > eventLineCount
                ? "This payable's amount is the sum of the lines below."
                : "This payable's amount is the sum of the lines below — change an event's amount and the payable follows."}
            </p>
          </div>
          <div>
            {lineItems.map((linked, index) => {
              const meta = coveredLineMeta(linked, detailByEventId)
              return (
                <div
                  key={linked.orderEventId ?? `${linked.source}-${index}`}
                  className="flex items-center justify-between gap-3 border-t border-border py-2 text-sm first:border-t-0"
                >
                  <div className="min-w-0">
                    <div className="truncate font-medium text-foreground">{linked.label}</div>
                    {meta.length > 0 ? (
                      <div className="tabular-nums text-xs text-muted-foreground">{meta.join(' · ')}</div>
                    ) : null}
                  </div>
                  <span className="shrink-0 font-medium tabular-nums">{operationalAmountLabel(linked.amount)}</span>
                </div>
              )
            })}
          </div>
          <div className="flex items-center justify-between border-t border-border pt-2">
            <span className="text-sm text-muted-foreground">Total</span>
            <span className="text-base font-semibold tabular-nums">{previewResult.amount.toString()}</span>
          </div>
        </Card>
      ) : null}

      <Card className="p-6 space-y-4">
        <h2 className="text-lg font-semibold text-foreground">Payment</h2>
        <PaymentView payment={previewResult} parentSupplierId={parentSupplierId} />
      </Card>

      {readOnly ? <PayableDetails payable={previewResult} /> : null}

      <div className="flex justify-end gap-2">
        {canSave ? (
          <PermissionGatedButton
            label="Save"
            permitted={hasSavePerm}
            workflowReady={canSave}
            pending={pending}
            onClick={handleSave}
          />
        ) : null}
        {canMarkPaid ? (
          <PermissionGatedButton
            label="Mark as Paid"
            permitted={hasCompletePerm}
            workflowReady={canMarkPaid}
            pending={pending}
            onClick={() => setMarkPaidOpen(true)}
            variant="outline"
          />
        ) : null}
        {canPayNow ? (
          <PermissionGatedButton
            label="Pay Now"
            permitted={hasCompletePerm}
            workflowReady={canPayNow}
            pending={pending}
            onClick={() => setConfirmAction('complete')}
            variant="outline"
          />
        ) : null}
        {canSchedule ? (
          <PermissionGatedButton
            label="Schedule Payment"
            permitted={hasPublishPerm}
            workflowReady={canSchedule}
            pending={pending}
            onClick={() => setConfirmAction('publish')}
          />
        ) : null}
      </div>

      {confirmAction && previewResult ? (
        <PayableCommitConfirmModal
          isOpen
          action={confirmAction}
          payable={previewResult}
          isCommitting={isCommitting}
          onClose={() => setConfirmAction(null)}
          onConfirm={() => {
            void (async () => {
              const result = confirmAction === 'complete' ? await handleComplete() : await handlePublish()
              if (result) {
                setConfirmAction(null)
              }
            })()
          }}
        />
      ) : null}

      <PayableMarkPaidModal
        open={markPaidOpen}
        isLoading={isCommitting}
        onClose={() => setMarkPaidOpen(false)}
        onConfirm={onMarkPaidConfirm}
      />
    </div>
  )
}
